import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [currentApp, setCurrentApp] = useState(null);
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Only check for app routes
  const isAppRoute = location.pathname.startsWith('/app/');
  const needsAppData = isAppRoute || location.pathname === '/app-select';

  // Get user's available apps only when authenticated and needed
  const { data: userApps } = useQuery({
    queryKey: ['userApps'],
    queryFn: async () => {
      const response = await api.get('/appmodule/user-apps');
      return response.data;
    },
    enabled: !authLoading && isAuthenticated && needsAppData, // Wait for auth to load first
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  // Determine current app from URL
  useEffect(() => {
    if (!userApps || userApps.length === 0) return;

    const path = location.pathname;
    let detectedApp = null;

    if (path.startsWith('/app/contracts')) {
      detectedApp = userApps.find(app => app.id === 'contract-intelligence');
    } else if (path.startsWith('/app/sbir')) {
      detectedApp = userApps.find(app => app.id === 'sbir-sttr');
    }

    if (detectedApp) {
      setCurrentApp(prevApp => {
        // Only update if actually different
        if (prevApp?.id !== detectedApp.id) {
          return detectedApp;
        }
        return prevApp;
      });
    }
  }, [location.pathname, userApps]); // Remove currentApp from dependencies

  // Set app when user selects one (for app selector)
  const selectApp = (app) => {
    setCurrentApp(app);
  };

  // Get navigation items for current app
  const getAppNavigation = () => {
    if (!currentApp) return [];

    switch (currentApp.id) {
      case 'contract-intelligence':
        return [
          { name: 'Dashboard', path: '/app/contracts/dashboard', icon: 'dashboard' },
          { name: 'Opportunities', path: '/app/contracts/opportunities', icon: 'opportunities' },
          { name: 'AI Assistant', path: '/app/contracts/ai-assistant', icon: 'ai' },
          { name: 'Competitive Analysis', path: '/app/contracts/competitive-analysis', icon: 'analysis' },
          { name: 'Intelligence', path: '/app/contracts/intelligence', icon: 'intelligence' },
          { name: 'Pipelines', path: '/app/contracts/pipelines', icon: 'pipelines' },
        ];
      case 'sbir-sttr':
        return [
          { name: 'Dashboard', path: '/app/sbir/dashboard', icon: 'dashboard' },
          { name: 'Solicitations', path: '/app/sbir/solicitations', icon: 'opportunities' },
          { name: 'Saved Searches', path: '/app/sbir/saved-searches', icon: 'search' },
          { name: 'Deadlines', path: '/app/sbir/deadlines', icon: 'calendar' },
        ];
      default:
        return [];
    }
  };

  // Check if current route is within an app
  const isInApp = () => {
    return location.pathname.startsWith('/app/');
  };

  // Check if current route is a shared page
  const isSharedPage = () => {
    const sharedPaths = ['/profile', '/account-settings', '/admin'];
    return sharedPaths.some(path => location.pathname.startsWith(path));
  };

  const value = {
    currentApp,
    userApps,
    selectApp,
    getAppNavigation,
    isInApp,
    isSharedPage,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;